import React, { useEffect, useState } from 'react'
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom'
import { ArrowLeft, Copy, Grid2X2, LogOut, MoreHorizontal, Plus, Search, Trash2, UserRound } from 'lucide-react'
import { configurationError, config } from './config.js'
import { getSupabase } from './auth/supabase.js'
import { getWebCanvasHost } from './web-canvas/host-singleton.js'

function useSession() {
  const [state, setState] = useState(config.e2e ? { loading: false, session: { user: { id: 'e2e-user', email: 'e2e@local.invalid' } } } : { loading: true, session: null })
  useEffect(() => {
    if (config.e2e) return undefined
    const sb = getSupabase()
    if (!sb) { setState({ loading: false, session: null }); return undefined }
    void sb.auth.getSession().then(({ data }) => setState({ loading: false, session: data.session }))
    const { data } = sb.auth.onAuthStateChange((_event, session) => setState({ loading: false, session }))
    return () => data.subscription.unsubscribe()
  }, [])
  return state
}

function Brand() {
  return <div className="brand"><span className="brand-mark"><Grid2X2 size={16} strokeWidth={1.8} /></span><span>Blaze</span></div>
}

function ConfigurationNotice() {
  return <main className="center-page"><section className="auth-card"><Brand /><h1>Web Canvas 尚未配置</h1><p>{configurationError()}</p><code>复制 .env.example 为 .env.local</code></section></main>
}

function LoginPage({ session }) {
  const navigate = useNavigate()
  const location = useLocation()
  const [mode, setMode] = useState('login')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const sb = getSupabase()

  useEffect(() => {
    if (!session) return
    const next = new URLSearchParams(location.search).get('next')
    navigate(next?.startsWith('/') && !next.startsWith('//') ? next : location.state?.from || '/canvases', { replace: true })
  }, [session, navigate, location.search, location.state])

  async function submit(event) {
    event.preventDefault(); setBusy(true); setMessage('')
    const result = mode === 'signup' ? await sb.auth.signUp({ email, password }) : await sb.auth.signInWithPassword({ email, password })
    setBusy(false)
    if (result.error) setMessage(result.error.message)
    else if (mode === 'signup' && !result.data.session) setMessage('验证邮件已发送，请完成验证后登录。')
  }

  async function google() {
    await sb.auth.signInWithOAuth({ provider: 'google', options: { redirectTo: `${config.appBaseUrl}/canvases` } })
  }

  return <main className="center-page">
    <section className="auth-card">
      <Brand />
      <div className="auth-heading"><h1>{mode === 'signup' ? '创建 Blaze 账户' : '登录 Web Canvas'}</h1><p>你的画布和素材会与桌面端安全同步。</p></div>
      <button className="secondary-btn full" onClick={google}>使用 Google 继续</button>
      <div className="auth-divider"><span>或</span></div>
      <form onSubmit={submit} className="auth-form">
        <label>邮箱<input className="dlg-input" type="email" required autoComplete="email" value={email} onChange={event => setEmail(event.target.value)} /></label>
        <label>密码<input className="dlg-input" type="password" required minLength={8} autoComplete={mode === 'signup' ? 'new-password' : 'current-password'} value={password} onChange={event => setPassword(event.target.value)} /></label>
        {message && <p className="form-message">{message}</p>}
        <button className="primary-btn full" disabled={busy}>{busy ? '请稍候…' : mode === 'signup' ? '注册' : '登录'}</button>
      </form>
      <div className="auth-links">
        <button onClick={() => setMode(mode === 'signup' ? 'login' : 'signup')}>{mode === 'signup' ? '已有账户？登录' : '没有账户？注册'}</button>
        {mode === 'login' && <button onClick={() => navigate('/reset-password')}>忘记密码</button>}
      </div>
    </section>
  </main>
}

function ResetPasswordPage() {
  const [email, setEmail] = useState('')
  const [message, setMessage] = useState('')
  const [recovery, setRecovery] = useState(false)
  const [password, setPassword] = useState('')
  const navigate = useNavigate()
  useEffect(() => {
    const sb = getSupabase()
    void sb.auth.getSession().then(({ data }) => setRecovery(Boolean(data.session && location.hash)))
    const { data } = sb.auth.onAuthStateChange(event => { if (event === 'PASSWORD_RECOVERY') setRecovery(true) })
    return () => data.subscription.unsubscribe()
  }, [])
  async function submit(event) {
    event.preventDefault()
    const sb = getSupabase()
    if (recovery) {
      const { error } = await sb.auth.updateUser({ password })
      setMessage(error ? error.message : '密码已更新，现在可以返回画布。')
      return
    }
    const { error } = await sb.auth.resetPasswordForEmail(email, { redirectTo: `${config.appBaseUrl}/reset-password` })
    setMessage(error ? error.message : '重置邮件已发送。')
  }
  return <main className="center-page"><section className="auth-card">
    <button className="back-link" onClick={() => navigate('/login')}><ArrowLeft size={15} />返回登录</button>
    <h1>{recovery ? '设置新密码' : '重置密码'}</h1><p>{recovery ? '请输入至少 8 位的新密码。' : '输入注册邮箱，我们会发送安全重置链接。'}</p>
    <form onSubmit={submit} className="auth-form">{recovery
      ? <label>新密码<input className="dlg-input" type="password" minLength={8} required value={password} onChange={event => setPassword(event.target.value)} /></label>
      : <label>邮箱<input className="dlg-input" type="email" required value={email} onChange={event => setEmail(event.target.value)} /></label>}<button className="primary-btn full">{recovery ? '更新密码' : '发送重置邮件'}</button></form>
    {message && <p className="form-message">{message}</p>}
  </section></main>
}

function CanvasCard({ canvas, onRename, onDuplicate, onDelete }) {
  const navigate = useNavigate()
  const [menu, setMenu] = useState(false)
  return <article className="canvas-card" onClick={() => navigate(`/canvas/${encodeURIComponent(canvas.id)}`)}>
    <div className="canvas-preview"><div className="canvas-preview-grid" /><Grid2X2 size={28} strokeWidth={1.25} /></div>
    <div className="canvas-card-body"><div><h2>{canvas.name}</h2><p>{canvas.itemCount || 0} 个元素 · {new Date(canvas.updatedAt).toLocaleDateString('zh-CN')}</p></div>
      <button className="icon-btn" aria-label="画布菜单" onClick={event => { event.stopPropagation(); setMenu(!menu) }}><MoreHorizontal size={17} /></button>
      {menu && <div className="card-menu" onClick={event => event.stopPropagation()}>
        <button onClick={() => onRename(canvas)}>重命名</button>
        <button onClick={() => onDuplicate(canvas)}><Copy size={14} />创建副本</button>
        <button className="danger" onClick={() => onDelete(canvas)}><Trash2 size={14} />删除</button>
      </div>}
    </div>
  </article>
}

function CanvasesPage({ session }) {
  const host = getWebCanvasHost()
  const navigate = useNavigate()
  const [canvases, setCanvases] = useState([])
  const [query, setQuery] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [account, setAccount] = useState(false)

  async function refresh() {
    setLoading(true)
    try { setCanvases(await host.canvases.list()); setError('') } catch (reason) { setError(reason.message || '画布加载失败') }
    finally { setLoading(false) }
  }
  useEffect(() => { void refresh() }, [])

  async function create() { const id = await host.canvases.create('Untitled'); location.href = `/canvas/${encodeURIComponent(id)}` }
  async function rename(canvas) { const name = prompt('画布名称', canvas.name)?.trim(); if (name) { await host.canvases.rename(canvas.id, name); await refresh() } }
  async function duplicate(canvas) { await host.canvases.duplicate(canvas.id); await refresh() }
  async function remove(canvas) { if (confirm(`删除“${canvas.name}”？`)) { await host.canvases.delete(canvas.id); await refresh() } }
  async function signOut() { await getSupabase()?.auth.signOut(); navigate('/login') }
  const filtered = canvases.filter(canvas => canvas.name.toLowerCase().includes(query.trim().toLowerCase()))

  return <main className="projects-page">
    <header className="projects-header"><Brand /><div className="header-actions"><button className="primary-btn" onClick={create}><Plus size={16} />新建画布</button><button className="avatar-btn" onClick={() => setAccount(!account)}>{session.user.email?.slice(0, 2).toUpperCase() || <UserRound size={16} />}</button></div>
      {account && <div className="account-menu"><p>{session.user.email}</p><button onClick={signOut}><LogOut size={14} />退出登录</button></div>}
    </header>
    <section className="projects-content"><div className="projects-title-row"><div><span className="eyebrow">WORKSPACE</span><h1>Canvases</h1><p>跨设备继续你的创作，视口位置仅保存在当前设备。</p></div><label className="app-search"><Search size={14} /><input value={query} onChange={event => setQuery(event.target.value)} placeholder="搜索画布" /></label></div>
      {error && <div className="error-banner">{error}</div>}
      {loading ? <div className="empty-state">正在加载…</div> : filtered.length ? <div className="canvas-grid">{filtered.map(canvas => <CanvasCard key={canvas.id} canvas={canvas} onRename={rename} onDuplicate={duplicate} onDelete={remove} />)}</div> : <div className="empty-state"><span className="empty-icon"><Grid2X2 size={28} /></span><h2>{query ? '没有匹配的画布' : '创建第一张画布'}</h2><p>{query ? '尝试其他搜索词。' : '浏览器中的修改会先保存到本机，再安全同步到云端。'}</p>{!query && <button className="primary-btn" onClick={create}><Plus size={16} />新建画布</button>}</div>}
    </section>
  </main>
}

function Guard({ state, children }) {
  const location = useLocation()
  if (state.loading) return <main className="center-page">正在加载…</main>
  if (!state.session) return <Navigate to="/login" state={{ from: location.pathname }} replace />
  return children
}

export function App() {
  const auth = useSession()
  if (configurationError()) return <ConfigurationNotice />
  if (!config.enabled) return <main className="center-page">Web Canvas 暂未开放。</main>
  return <Routes>
    <Route path="/login" element={<LoginPage session={auth.session} />} />
    <Route path="/reset-password" element={<ResetPasswordPage />} />
    <Route path="/canvases" element={<Guard state={auth}><CanvasesPage session={auth.session} /></Guard>} />
    <Route path="*" element={<Navigate to={auth.session ? '/canvases' : '/login'} replace />} />
  </Routes>
}
